import type { KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  toneBackgroundImage,
  topicById,
  type LearnsTone,
  type LearnsTopicId,
} from '@/constants/learns-data';
import { usePaywall } from '@/hooks/usePaywall';
import { PaywallLock } from '@/components/paywall/PaywallLock';
import type { ArticleView } from '@/types/api';

/**
 * LearnsArticleGrid — 3-column grid of article cards.
 *
 * The parent passes in the already-filtered article list (topic filter
 * lives at the page level so the count in the section header stays in
 * sync). No empty-state copy is rendered — the grid just collapses.
 */
export interface LearnsArticleGridProps {
  articles: ArticleView[];
}

export function LearnsArticleGrid({ articles }: LearnsArticleGridProps) {
  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {articles.map((article) => (
        <ArticleCard key={article.id} article={article} />
      ))}
    </div>
  );
}

// -- Card ----------------------------------------------------------------

function ArticleCard({ article }: { article: ArticleView }) {
  const navigate = useNavigate();
  const { isPaywalled, openPaywall } = usePaywall();
  const topic = topicById(article.topic as LearnsTopicId);
  const TopicIcon = topic.icon;

  // Free-tier: list every article so the catalogue still feels alive,
  // but every click into a non-featured card opens the paywall instead
  // of navigating. The featured card on the page above is exempt — it's
  // the free taste.
  const go = () => {
    if (isPaywalled) {
      openPaywall('learn-article');
      return;
    }
    navigate(`/learns/articles/${article.slug}`);
  };

  const handleKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      go();
    }
  };

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={go}
      onKeyDown={handleKey}
      aria-label={
        isPaywalled
          ? `${article.title}. Locked — pick a plan to unlock.`
          : `${article.title}. ${article.mins} minute read.`
      }
      className="group flex cursor-pointer flex-col transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-[2px] hover:shadow-[0_6px_20px_rgba(31,27,22,0.06),0_1px_2px_rgba(31,27,22,0.04)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2E8A8A]/40 focus-visible:ring-offset-2"
      style={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #EFE5D2',
        borderRadius: '16px',
        overflow: 'hidden',
      }}
    >
      {/* --- Image area ---
          When an admin has uploaded a thumbnail we render it edge-to-edge
          via an absolutely-positioned `<img>`; otherwise we fall back to
          the deterministic tone-stripe background so the card never
          collapses on shape. */}
      <div
        style={{
          position: 'relative',
          aspectRatio: '16 / 10',
          backgroundImage: article.thumbnailImage
            ? undefined
            : toneBackgroundImage(article.tone as LearnsTone),
          backgroundColor: article.thumbnailImage ? '#F4ECE0' : undefined,
          filter: isPaywalled ? 'grayscale(0.35)' : undefined,
          overflow: 'hidden',
        }}
      >
        {article.thumbnailImage ? (
          <img
            src={article.thumbnailImage}
            alt=""
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
            aria-hidden
          />
        ) : null}
        {/* Topic chip overlay */}
        <span
          className="inline-flex items-center"
          style={{
            position: 'absolute',
            left: '12px',
            top: '12px',
            gap: '5px',
            backgroundColor: 'rgba(255,255,255,0.92)',
            color: topic.accent,
            fontFamily: 'Inter, sans-serif',
            fontSize: '10px',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            padding: '4px 10px',
            borderRadius: '999px',
          }}
        >
          <TopicIcon width={11} height={11} strokeWidth={2} color={topic.accent} aria-hidden />
          {topic.label}
        </span>

        {/* Locked badge — top-right of the image area. Mirrors the topic
            chip styling so it feels like a sibling label rather than a
            tacked-on overlay. */}
        {isPaywalled ? (
          <div style={{ position: 'absolute', right: '12px', top: '12px' }}>
            <PaywallLock />
          </div>
        ) : null}
      </div>

      {/* --- Body --- */}
      <div className="flex flex-col" style={{ padding: '16px 18px 18px', flex: 1 }}>
        <h3
          style={{
            fontFamily: '"DM Serif Display", serif',
            fontWeight: 400,
            fontSize: '20px',
            lineHeight: 1.15,
            color: '#1F1B16',
            margin: '0 0 8px',
          }}
        >
          {article.title}
        </h3>
        <p
          style={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: '13px',
            lineHeight: 1.5,
            color: '#3D342A',
            margin: 0,
            flexGrow: 1,
          }}
        >
          {article.summary}
        </p>

        {/* Meta footer */}
        <div
          className="flex items-center justify-between"
          style={{
            marginTop: '14px',
            paddingTop: '14px',
            borderTop: '1px solid #EFE5D2',
            fontFamily: 'Inter, sans-serif',
            fontSize: '12px',
            color: '#8A7F72',
          }}
        >
          <span className="inline-flex items-center" style={{ gap: '10px' }}>
            <span aria-hidden>🕒</span>
            <span>{article.mins} min</span>
          </span>
          <span
            style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              color: isPaywalled ? '#B45309' : '#1F6A6A',
            }}
          >
            {isPaywalled ? 'Unlock to read →' : 'Read →'}
          </span>
        </div>
      </div>
    </div>
  );
}
